import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { ArrowRight, Eye, Heart, TrendingUp, Users, Zap, ExternalLink, CheckCircle, Target, Lightbulb, BarChart3, Palette, Megaphone, Globe, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

const CaseStudiesPage = () => {
  const navigate = useNavigate();

  const portfolioStats = [
    { label: "Projets réalisés", value: "150+", icon: Award },
    { label: "Clients satisfaits", value: "50+", icon: Heart },
    { label: "Années d'expérience", value: "8+", icon: TrendingUp },
    { label: "Secteurs accompagnés", value: "20+", icon: Globe }
  ];

  const speedInscriCase = {
    id: 1,
    title: "Speed-Inscri",
    subtitle: "Création, développement et transformation digitale d'une startup EdTech à impact international",
    category: "Branding & Communication 360°",
    tags: ["Branding", "Communication 360°", "Digital", "Stratégie"],
    results: [
      { metric: "Déploiement", value: "10 pays", icon: Globe },
      { metric: "Étudiants accompagnés", value: "+1200", icon: Users },
      { metric: "Notoriété", value: "+480%", icon: TrendingUp },
      { metric: "Taux de conversion", value: "38%", icon: Target }
    ],
    image: "/images/speed-inscri-preview.jpg",
    duration: "2020 - aujourd'hui",
  };

  const expertise = [
    {
      icon: Eye,
      title: "Identité Visuelle",
      description: "Création de logos, chartes graphiques et univers visuels cohérents",
      color: "blue",
      link: "/identite-visuelle"
    },
    {
      icon: Heart,
      title: "Stratégie de Marque",
      description: "Positionnement, territoire de marque et architecture de communication",
      color: "pink",
      link: "/strategie-marque"
    },
    {
      icon: TrendingUp,
      title: "Communication 360°",
      description: "Stratégies intégrées print, digital, événementiel et relations presse",
      color: "blue",
      link: "/communication-360"
    },
    {
      icon: Users,
      title: "Communication Digitale",
      description: "Social media, content marketing et campagnes publicitaires digitales",
      color: "pink",
      link: "/communication-digitale"
    }
  ];

  const methodologies = [
    {
      icon: Target,
      title: "Audit & Diagnostic",
      description: "Analyse approfondie de votre écosystème communicationnel",
      steps: ["Audit de l'existant", "Analyse concurrentielle", "Diagnostic stratégique", "Recommandations"]
    },
    {
      icon: Lightbulb,
      title: "Stratégie & Concept",
      description: "Développement de concepts créatifs sur-mesure",
      steps: ["Brief créatif", "Idéation", "Conceptualisation", "Validation"]
    },
    {
      icon: Palette,
      title: "Création & Production",
      description: "Réalisation de vos supports de communication",
      steps: ["Design", "Rédaction", "Production", "Déclinaisons"]
    },
    {
      icon: BarChart3,
      title: "Déploiement & Suivi",
      description: "Mise en œuvre et optimisation continue",
      steps: ["Lancement", "Diffusion", "Mesure", "Optimisation"]
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy-50 to-pink-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/80 text-blue-600 px-6 py-3 rounded-full text-sm font-medium mb-8 animate-fade-in">
              <Award size={16} />
              Portfolio & Réalisations
            </div>
            
            <h1 className="text-6xl md:text-7xl font-light text-navy-900 mb-8 leading-tight">
              Mes <span className="font-semibold text-blue-600">réalisations</span>
              <br />
              <span className="text-4xl md:text-5xl text-navy-600">parlent pour moi</span>
            </h1>
            <p className="text-xl text-navy-600 mb-12 font-light leading-relaxed max-w-3xl mx-auto">
              Découvrez comment j'accompagne mes clients dans leur transformation 
              communicationnelle avec des stratégies sur-mesure et des résultats mesurables.
            </p>
            
            {/* Portfolio Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {portfolioStats.map((stat, index) => (
                <div key={index} className="text-center animate-scale-in" style={{animationDelay: `${index * 0.1}s`}}>
                  <div className="bg-white/80 p-6 rounded-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <stat.icon className="text-blue-600 mx-auto mb-3" size={32} />
                    <div className="text-3xl font-bold text-navy-900 mb-1">{stat.value}</div>
                    <div className="text-sm text-navy-600 font-medium">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-navy-900 mb-6">
                Ce que je <span className="font-semibold text-blue-600">sais faire</span>
              </h2>
              <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
                Une expertise complète au service de votre communication
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
              {expertise.map((item, index) => (
                <Link 
                  key={index}
                  to={item.link}
                  className="text-center group animate-fade-in hover:-translate-y-1 transition-all duration-300" 
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  <div className={`bg-${item.color}-600 text-white p-6 rounded-none w-20 h-20 mx-auto mb-6 flex items-center justify-center group-hover:bg-navy-900 transition-colors duration-300`}>
                    <item.icon size={32} />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">{item.title}</h3>
                  <p className="text-navy-600 font-light text-sm leading-relaxed mb-4">{item.description}</p>
                  <div className="flex items-center justify-center gap-2 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-sm font-medium">En savoir plus</span>
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </Link>
              ))}
            </div>

            {/* Methodologies */}
            <div className="bg-navy-50/50 p-12 rounded-none">
              <h3 className="text-3xl font-light text-navy-900 mb-12 text-center">
                Ma <span className="font-semibold text-blue-600">méthodologie</span>
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {methodologies.map((method, index) => (
                  <div key={index} className="group">
                    <div className="bg-white p-6 rounded-none shadow-sm hover:shadow-lg transition-all duration-300 h-full">
                      <div className="bg-blue-600 text-white p-4 rounded-none w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-pink-500 transition-colors duration-300">
                        <method.icon size={24} />
                      </div>
                      <h4 className="text-lg font-semibold text-navy-900 mb-3">{method.title}</h4>
                      <p className="text-navy-600 font-light text-sm mb-4">{method.description}</p>
                      <ul className="space-y-2">
                        {method.steps.map((step, stepIndex) => (
                          <li key={stepIndex} className="flex items-center gap-2 text-xs text-navy-600">
                            <CheckCircle size={12} className="text-blue-600 flex-shrink-0" />
                            {step}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-light text-navy-900 mb-6">
                Portfolio <span className="font-semibold text-blue-600">sélectionné</span>
              </h2>
              <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto mb-8">
                Une sélection de mes collaborations les plus significatives
              </p>
            </div>

            <div 
              className="bg-white rounded-lg shadow-xl overflow-hidden cursor-pointer transform transition-transform duration-300 hover:scale-[1.02]"
              onClick={() => navigate('/speed-inscri')}
            >
              <div className="p-8 md:p-12">
                <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  {speedInscriCase.category}
                </div>
                
                <h3 className="text-3xl font-semibold text-navy-900 mb-4">
                  {speedInscriCase.title}
                </h3>
                <p className="text-xl text-navy-600 mb-8">
                  {speedInscriCase.subtitle}
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                  {speedInscriCase.results.map((result, index) => (
                    <div key={index} className="bg-navy-50 p-6 rounded-lg">
                      <result.icon className="text-blue-600 mb-3" size={24} />
                      <div className="text-2xl font-bold text-navy-900 mb-1">{result.value}</div>
                      <div className="text-sm text-navy-600">{result.metric}</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 text-blue-600 font-medium">
                  Voir le cas d'étude complet <ArrowRight size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-navy-900 to-blue-900">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-light text-white mb-6">
              Votre projet mérite une communication
              <span className="font-semibold text-pink-400"> d'exception</span>
            </h2>
            <p className="text-xl text-navy-200 mb-8 font-light">
              Discutons de vos objectifs et créons ensemble une stratégie qui vous démarque.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:julycia.pro@gmail.com"
                className="bg-pink-500 text-white px-8 py-4 font-medium hover:bg-pink-600 transition-colors duration-300 group inline-flex items-center justify-center gap-2"
              >
                Démarrer un projet
                <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <button 
                onClick={() => {
                  const phoneNumber = "+33643581540";
                  const message = "Bonjour Julycia ! J'aimerais discuter d'un projet de communication avec vous après avoir vu vos réalisations.";
                  const whatsappUrl = `https://wa.me/${phoneNumber.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="bg-green-500 text-white px-8 py-4 font-medium hover:bg-green-600 transition-colors duration-300 group inline-flex items-center justify-center gap-2"
              >
                Discuter sur WhatsApp
                <ExternalLink size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CaseStudiesPage;
